const TOLERANCE = 2;

/**
 * @typedef {{ key: string, params?: Record<string, unknown> }} MessageDescriptor
 */

function splitStock(stock, cut) {
  if (cut.axis === "vertical") {
    const leftLength = cut.position - stock.x;
    const rightLength = stock.x + stock.length - cut.position;
    const pieces = [];
    if (leftLength > TOLERANCE) {
      pieces.push({ x: stock.x, y: stock.y, length: leftLength, width: stock.width });
    }
    if (rightLength > TOLERANCE) {
      pieces.push({ x: cut.position, y: stock.y, length: rightLength, width: stock.width });
    }
    return pieces;
  }

  const topWidth = cut.position - stock.y;
  const bottomWidth = stock.y + stock.width - cut.position;
  const pieces = [];
  if (topWidth > TOLERANCE) {
    pieces.push({ x: stock.x, y: stock.y, length: stock.length, width: topWidth });
  }
  if (bottomWidth > TOLERANCE) {
    pieces.push({ x: stock.x, y: cut.position, length: stock.length, width: bottomWidth });
  }
  return pieces;
}

/**
 * @param {object|null} cut
 * @param {number} boardLength
 * @param {number} boardWidth
 * @returns {MessageDescriptor}
 */
export function buildCutSimulationCaptionDescriptor(cut, boardLength, boardWidth) {
  if (!cut) {
    return { key: "workshop.caption.start" };
  }

  const segmentLength = cut.end - cut.start;
  const orientationCapKey = cut.axis === "horizontal" ? "horizontal" : "vertical";
  const fullSpan = cut.axis === "horizontal" ? boardLength : boardWidth;
  const spanKey = cut.axis === "horizontal" ? "length" : "width";
  const fullLine = Math.abs(segmentLength - fullSpan) <= TOLERANCE + 1;
  const position = Math.round(cut.position);

  if (cut.kind === "strip-rip") {
    return {
      key: "workshop.caption.stripRip",
      params: { position, orientationCapKey },
    };
  }
  if (cut.kind === "strip-cross") {
    return {
      key: "workshop.caption.stripCross",
      params: { position },
    };
  }
  if (fullLine) {
    return {
      key: "workshop.caption.fullLine",
      params: { position, orientationCapKey, spanKey },
    };
  }
  return {
    key: "workshop.caption.partial",
    params: { position, orientationCapKey },
  };
}

/**
 * @param {object|null} cut
 * @param {object|null} stock
 * @param {number} boardLength
 * @param {number} boardWidth
 * @returns {{ title: MessageDescriptor, detailParts: MessageDescriptor[] }}
 */
export function buildCutWorkshopInstructionDescriptor(cut, stock, boardLength, boardWidth) {
  if (!cut) {
    return {
      title: { key: "workshop.complete.title" },
      detailParts: [{ key: "workshop.complete.detail" }],
    };
  }

  const position = Math.round(cut.position);
  const orientationKey = cut.axis === "horizontal" ? "horizontal" : "vertical";
  const segmentLength = cut.end - cut.start;
  const fullSpan = cut.axis === "horizontal" ? boardLength : boardWidth;
  const fullLine = Math.abs(segmentLength - fullSpan) <= TOLERANCE + 1;

  let titleKey = "workshop.cut.partial";
  if (cut.kind === "strip-rip") {
    titleKey = "workshop.cut.stripRip";
  } else if (cut.kind === "strip-cross") {
    titleKey = "workshop.cut.stripCross";
  } else if (fullLine) {
    titleKey = "workshop.cut.fullLine";
  }

  const detailParts = [];
  if (stock) {
    detailParts.push({
      key: "workshop.detail.workPiece",
      params: {
        sizeLength: Math.round(stock.length),
        sizeWidth: Math.round(stock.width),
      },
    });

    const pieces = splitStock(stock, cut);
    if (pieces.length === 2) {
      const [first, second] = pieces;
      if (cut.axis === "vertical") {
        detailParts.push({
          key: "workshop.detail.afterVertical",
          params: {
            first: Math.round(first.length),
            second: Math.round(second.length),
          },
        });
      } else {
        detailParts.push({
          key: "workshop.detail.afterHorizontal",
          params: {
            first: Math.round(first.width),
            second: Math.round(second.width),
          },
        });
      }
    }
  }

  return {
    title: {
      key: titleKey,
      params: { position, orientationKey },
    },
    detailParts,
  };
}
